import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import styled, { css, styled as styled$1 } from "styled-components";
import { colors, s8, s16, shadowS, s4, s24, radius, shadowM, spacing, space } from "@ardoq/design-tokens";
import { button, Header5, header4, Header4, InlineText, Header2, caption, Text, Link, Paragraph } from "@ardoq/typography";
import { focusableCSS, Space } from "@ardoq/style-helpers";
import { ICON_SELECTOR, SpinnerIcon, DragButton, Icon, IconSize, IconName, FilterListIcon, SvgImage, SvgImageName } from "@ardoq/icons";
import { COLOR_BAR_SELECTOR, ColorBar, IslandHeader } from "@ardoq/page-layout";
import { DragAndDropCardWrapper } from "@ardoq/drag-and-drop";
import { FlexBox, Box, Stack } from "@ardoq/layout";
import { ChipsList, FilterChip, ChipsSeparator, Chip, IconBadge, StatusType, ErrorNotification } from "@ardoq/status-ui";
import { SearchInput, FieldWrapper as FieldWrapper$1, HelperTextWrapper, InputErrorMessage, InputWarningMessage, commonInputStyle, LABEL_SELECTOR, Field, useTextAreaAutosize, InputWrapper, ReadOnlyStyle, getChildElementId, FormErrorIcon, FormWarningIcon } from "@ardoq/forms";
import { BUTTON_SELECTOR, TertiaryButton, BrandButton, IconButton } from "@ardoq/button";
import React, { Fragment as Fragment$1, useState, useImperativeHandle, useId, useEffect, cloneElement } from "react";
import { DoqLoadingAnimation, DoqType, DoqSize } from "@ardoq/doq";
import { WithPopover } from "@ardoq/popovers";
import { pushStackPage, popStackPageByName } from "@ardoq/stack-page-manager";
import { handleEscapeButtonPressed, FullscreenDiv, resolveBoolean } from "@ardoq/modal";
import { useKeydown } from "@ardoq/hooks";
import { currentYear } from "@ardoq/date-time";
import { getArdoqErrorMessage, getArdoqErrorTraceId } from "@ardoq/common-helpers";
const BUTTON_WIDTH = 245;
const BUTTON_HEIGHT = 72;
const LoadingIconWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: calc(50% - 12px);
`;
const ButtonStyle = styled.button`
  ${button};
  ${focusableCSS};
  width: ${BUTTON_WIDTH}px;
  border: 1px solid ${colors.borderAction};
  border-radius: ${s8};
  &:hover {
    border-color: ${colors.borderActionHover};
  }
  &:active {
    border-color: ${colors.borderActionActive};
    background-color: ${colors.surfaceActionSecondaryPressed};
  }
  height: ${BUTTON_HEIGHT}px;
  padding: ${s16};
  background: ${colors.surfaceDefault};
  box-shadow: ${shadowS};
  color: ${colors.textModerate};
  display: flex;
  position: relative;
  align-items: center;
  cursor: pointer;
  ${ICON_SELECTOR} {
    color: ${colors.icon};
  }
  ${ICON_SELECTOR}:first-child {
    margin-right: ${s16};
  }
  ${ICON_SELECTOR} ~ ${ICON_SELECTOR} {
    /* Selecting second icon using sibling selector */
    margin-left: auto;
  }
  ${({ $isLoading }) => $isLoading && css`
      cursor: default;
      color: ${colors.textDisabled};
      pointer-events: none;
      border-color: ${colors.borderActionDisabled};
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
      }
    `}
  ${({ $isDisabled }) => $isDisabled && css`
      color: ${colors.textDisabled};
      border-color: ${colors.borderActionDisabled};
      background-color: ${colors.surfaceDisabled};
      box-shadow: none;
      pointer-events: none;
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
      }
    `}
`;
const BigButton = ({
  children,
  isLoading,
  isDisabled,
  onClick,
  clickId,
  dataTestId,
  className
}) => {
  return /* @__PURE__ */ jsxs(
    ButtonStyle,
    {
      className,
      type: "button",
      onClick,
      $isLoading: Boolean(isLoading),
      $isDisabled: Boolean(isDisabled),
      "data-click-id": clickId,
      "data-test-id": dataTestId,
      disabled: isDisabled,
      children: [
        children,
        isLoading && /* @__PURE__ */ jsx(LoadingIconWrapper, { children: /* @__PURE__ */ jsx(SpinnerIcon, {}) })
      ]
    }
  );
};
const ColorBarBigButton = styled(BigButton)`
  background-color: ${({ backgroundColor }) => backgroundColor ? colors[backgroundColor] : colors.surfaceStatusNeutral};
  gap: ${s16};
  border: 1px solid ${colors.borderAction};
  width: ${({ width }) => width ?? "unset"};
  ${({ height }) => height && css`
      height: ${height};
    `};
  ${COLOR_BAR_SELECTOR} {
    height: 100%;
    margin: 0;
    flex-shrink: 0;
  }
  &:disabled {
    background-color: ${colors.surfaceDisabled};
  }
`;
const IslandDragAndDropCardWrapper = styled(DragAndDropCardWrapper)`
  flex: 1;
  align-items: center;
  width: 100%;
  ${DragButton} {
    padding-left: ${s4};
  }
`;
const BorderWrapper = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.borderAction};
  cursor: pointer;
  &:only-child {
    border-bottom: 0;
  }
`;
const ClickableIslandHeader = ({
  barColor,
  dragAndDropCardId,
  onClick,
  children,
  ...headerProps
}) => /* @__PURE__ */ jsxs(BorderWrapper, { onClick, "data-test-id": "expand-island-header-button", children: [
  barColor && /* @__PURE__ */ jsx(ColorBar, { $barColor: barColor }),
  /* @__PURE__ */ jsx(
    IslandDragAndDropCardWrapper,
    {
      dragAndDropCardId,
      dragButtonAlwaysVisible: true,
      dragButtonPosition: "left",
      children: /* @__PURE__ */ jsx(IslandHeader, { ...headerProps, children })
    }
  )
] });
const LineSeparatorWithText = ({ text }) => {
  return /* @__PURE__ */ jsxs(FlexBox, { align: "center", gap: "small", children: [
    /* @__PURE__ */ jsx(Line, {}),
    /* @__PURE__ */ jsx(Header5, { color: "textSubtle", children: /* @__PURE__ */ jsx(NoWrapText, { children: text }) }),
    /* @__PURE__ */ jsx(Line, {})
  ] });
};
const Line = styled.div`
  border-bottom: 1px solid ${colors.borderAction};
  height: 1px;
  width: 100%;
`;
const NoWrapText = styled.span`
  text-wrap: nowrap;
`;
const SEARCH_AND_FILTER_BAR_HEIGHT = "68px";
const SearchAndFilterBarContainer = styled(Space)`
  height: ${({ $containerHeight }) => $containerHeight ? $containerHeight : SEARCH_AND_FILTER_BAR_HEIGHT};
  padding: ${({ $padding }) => $padding ? $padding : `${s16}`};
  width: 100%;

  ${BUTTON_SELECTOR} {
    flex: 0 0 auto;
  }

  border-bottom: ${({ $disableBottomBorder }) => $disableBottomBorder ? "none" : `1px solid ${colors.borderAction}`};
`;
const ChipsListAndClearAllButtonContainer = styled(Space)`
  margin-right: auto; /* This is to push the filter button to the right */
  min-width: 400px;
`;
const getChipKey = (value, index, type) => {
  const stringifiedValue = value == null ? void 0 : value.toString();
  const valueBasedKey = stringifiedValue ? stringifiedValue : index.toString();
  return `${valueBasedKey}-${type}`;
};
const SearchAndFilterBar = ({
  activeFilters,
  removeFilter,
  clearAllFilters,
  onSearch,
  searchValue,
  onFilterButtonClick,
  searchDisabled,
  barContainerProps,
  clearAllButtonDisabled
}) => /* @__PURE__ */ jsxs(SearchAndFilterBarContainer, { ...barContainerProps, children: [
  !searchDisabled && onSearch && /* @__PURE__ */ jsx(
    SearchInput,
    {
      onValueChange: onSearch,
      placeholder: "Search by data name",
      value: searchValue ?? ""
    }
  ),
  /* @__PURE__ */ jsxs(ChipsListAndClearAllButtonContainer, { $gap: "s8", $align: "center", children: [
    /* @__PURE__ */ jsx(ChipsList, { children: activeFilters.map(
      ({ label, value, iconName, type, rightIconName, isDisabled }, index) => /* @__PURE__ */ jsx(
        FilterChip,
        {
          iconName,
          onClose: () => removeFilter(value, type),
          isDisabled,
          children: /* @__PURE__ */ jsxs(FlexBox, { align: "center", gap: "xsmall", children: [
            label,
            rightIconName ? /* @__PURE__ */ jsx(Icon, { iconName: rightIconName, iconSize: IconSize.SMALL }) : null
          ] })
        },
        getChipKey(value, index, type)
      )
    ) }),
    Boolean(activeFilters.length) && !clearAllButtonDisabled && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(ChipsSeparator, {}),
      /* @__PURE__ */ jsx(Chip, { onClick: clearAllFilters, "data-test-id": "clear-all-chip", children: "Clear all" })
    ] })
  ] }),
  /* @__PURE__ */ jsxs(
    TertiaryButton,
    {
      onClick: onFilterButtonClick,
      dataTestId: "search-and-filter-bar-filter-button",
      children: [
        activeFilters.length > 0 ? /* @__PURE__ */ jsx(
          IconBadge,
          {
            iconName: IconName.FILTER_LIST,
            statusType: StatusType.NEW,
            style: { margin: `0 ${s8}` },
            iconStyle: { margin: 0 }
          }
        ) : /* @__PURE__ */ jsx(FilterListIcon, {}),
        `Filter ${activeFilters.length ? ` (${activeFilters.length})` : ""}`
      ]
    }
  )
] });
const SwitchContainer = styled.div`
  display: flex;
  vertical-align: middle;
  width: 80px;
  height: ${s24};
  border-radius: 100px;
  background-color: ${colors.surfaceStatusNeutral};
  gap: ${s4};
  ${({ $isDisabled }) => $isDisabled && css`
      pointer-events: none;
      background-color: ${colors.surfaceDisabled};
    `}
`;
const Radio = styled.input`
  display: none;
`;
const getColorCss = (isSelected, switchPosition) => {
  if (!isSelected) return "";
  switch (switchPosition) {
    case "rejected":
      return css`
        color: ${colors.textOnInvertedStrong};
        background-color: ${colors.iconError};
      `;
    case "approved":
      return css`
        color: ${colors.textOnInvertedStrong};
        background-color: ${colors.iconSuccess};
      `;
    case "pending":
      return css`
        background-color: ${colors.iconOnInverted};
      `;
  }
};
const Label$1 = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${s24};
  height: ${s24};
  border-radius: 100%;
  transition: all 0.3s ease-in-out;
  color: ${colors.textSubtle};
  ${({ $isSelected, $switchPosition }) => getColorCss($isSelected, $switchPosition)}
  ${({ $isSelected }) => !$isSelected && css`
      &:hover {
        background-color: ${colors.surfaceActionGhostHover};
        color: ${colors.textSubtle};
      }
    `}

  ${({ $isDisabled }) => $isDisabled && css`
      color: ${colors.textDisabled};
    `}
`;
const switchPositions = ["rejected", "pending", "approved"];
const getIconForSwitchPosition = (switchPosition) => {
  switch (switchPosition) {
    case "rejected":
      return IconName.CLOSE;
    case "approved":
      return IconName.CHECK_MARK;
    case "pending":
      return IconName.REMOVE;
  }
};
const TriStateSwitch = ({
  name,
  value,
  dataTestId,
  formSize,
  alignItemsCenter,
  helperText,
  errorMessage,
  warningMessage,
  isDisabled,
  onChange
}) => {
  return /* @__PURE__ */ jsxs(
    FieldWrapper$1,
    {
      "data-test-id": dataTestId,
      $formSize: formSize,
      $alignItemsCenter: alignItemsCenter,
      children: [
        helperText && /* @__PURE__ */ jsx(HelperTextWrapper, { children: helperText }),
        /* @__PURE__ */ jsx(SwitchContainer, { $isDisabled: isDisabled, children: switchPositions.map((switchPosition) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
          /* @__PURE__ */ jsx(
            Radio,
            {
              onChange: () => onChange(switchPosition),
              name,
              id: `${name}-${switchPosition}-${value}`,
              type: "radio",
              value: switchPosition
            }
          ),
          /* @__PURE__ */ jsx(
            Label$1,
            {
              $isSelected: value === switchPosition,
              htmlFor: `${name}-${switchPosition}-${value}`,
              $switchPosition: switchPosition,
              $isDisabled: isDisabled,
              children: /* @__PURE__ */ jsx(
                Icon,
                {
                  iconSize: IconSize.SMALL,
                  iconName: getIconForSwitchPosition(switchPosition)
                }
              )
            }
          )
        ] }, `${name}-${switchPosition}-${value}`)) }),
        errorMessage && /* @__PURE__ */ jsx(InputErrorMessage, { children: errorMessage }),
        !errorMessage && warningMessage && /* @__PURE__ */ jsx(InputWarningMessage, { children: warningMessage })
      ]
    }
  );
};
const Badge = styled.span`
  background-color: ${colors.iconError};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${s16};
  height: ${s16};
  color: ${colors.surfaceDefault};
  font-size: ${s8};
`;
const BadgeWithCounter = ({ count, style }) => {
  if (!count) return null;
  return /* @__PURE__ */ jsx(Badge, { style, children: count });
};
const CounterTextWrapper = styled(InputWarningMessage)`
  color: ${colors.textSubtle};
`;
const TextAreaStyle = styled.textarea`
  &::placeholder {
    color: ${colors.textSubtle};
  }
  ${commonInputStyle};
  color: inherit;
  resize: none;
  ${({ $height }) => $height ? `height: ${$height};` : ""}
`;
const FieldWrapper = styled(Field)`
  ${LABEL_SELECTOR} {
    ${header4};
  }

  ${HelperTextWrapper} {
    font-size: 12px;
  }
`;
const TextAreaWithCounter = React.forwardRef(
  ({
    autoFocus,
    isDisabled,
    isReadOnly,
    indicator,
    warningMessage: warningMessageProp,
    errorMessage,
    helperText,
    label,
    hideWarningIcon,
    value,
    defaultValue,
    fixedHeight,
    minRows,
    formSize,
    onChange,
    onValueChange,
    onFocus,
    onBlur,
    popoverHelpContent,
    characterLimit,
    characterLimitWarning,
    dataTestId,
    ...props
  }, forwardedRef) => {
    const [characterCount, setCharacterCount] = useState(
      (value == null ? void 0 : value.length) || (defaultValue == null ? void 0 : defaultValue.length) || 0
    );
    const { ref } = useTextAreaAutosize(value);
    useImperativeHandle(forwardedRef, () => ref.current);
    const generatedTextInputId = useId();
    const id = props.id ?? generatedTextInputId;
    const isOverCharacterLimit = Boolean(
      characterLimit && characterCount > characterLimit
    );
    const counterText = characterLimit && `${characterCount}/${characterLimit} characters`;
    const counterWarningMessage = isOverCharacterLimit ? `${counterText}${characterLimitWarning ? `. ${characterLimitWarning}` : ""}` : void 0;
    const warningMessage = warningMessageProp || counterWarningMessage;
    const hasError = Boolean(errorMessage);
    const hasWarning = !hasError && Boolean(warningMessage);
    const onInputChange = (e) => {
      const newValue = e.currentTarget.value;
      setCharacterCount(newValue.length);
      onValueChange == null ? void 0 : onValueChange(newValue);
      onChange == null ? void 0 : onChange(e);
    };
    const styleProps = {
      $isDisabled: isDisabled,
      $hasError: hasError,
      $hasWarning: hasWarning,
      $hideWarningIcon: hideWarningIcon
    };
    return /* @__PURE__ */ jsxs(
      FieldWrapper,
      {
        label,
        inputId: id,
        helperText,
        isDisabled,
        indicator,
        errorMessage,
        warningMessage: warningMessage || counterWarningMessage,
        popoverHelpContent,
        formSize,
        children: [
          /* @__PURE__ */ jsxs(InputWrapper, { ...styleProps, children: [
            isReadOnly ? /* @__PURE__ */ jsx(ReadOnlyStyle, { children: value }) : /* @__PURE__ */ jsx(
              TextAreaStyle,
              {
                ...props,
                ...styleProps,
                onChange: onInputChange,
                onFocus,
                onBlur,
                value,
                defaultValue,
                ref: fixedHeight ? void 0 : ref,
                $height: fixedHeight ? "100%" : "auto",
                disabled: isDisabled,
                rows: minRows || 1,
                "data-testid": dataTestId,
                "data-test-id": dataTestId,
                autoFocus,
                id,
                "aria-describedby": helperText ? getChildElementId(id, "help-text") : void 0
              }
            ),
            hasError && /* @__PURE__ */ jsx(FormErrorIcon, {}),
            hasWarning && !hideWarningIcon && /* @__PURE__ */ jsx(FormWarningIcon, {})
          ] }),
          counterText && !hasWarning && !hasError && /* @__PURE__ */ jsx(CounterTextWrapper, { children: counterText })
        ]
      }
    );
  }
);
const DEFAULT_ERROR_MESSAGE = "An error occurred while generating the description. Please try contacting support if the issue persists.";
const getButtonProps = ({
  isLoading,
  hasError,
  currentDescription,
  componentName,
  componentTypeName,
  textName = "description"
}) => {
  if (!componentName) {
    return {
      label: "Generate " + textName,
      icon: "✨ ",
      isDisabled: true,
      ["data-tooltip-text"]: `Add the ${componentTypeName.toLowerCase()} name
      in the name question to let AI generate a ${textName} automatically.`
    };
  }
  if (isLoading) {
    return {
      label: "Generating the " + textName,
      isLoading: true
    };
  } else if (hasError) {
    return {
      label: "Generate " + textName,
      hasError,
      icon: "✨ "
    };
  } else if (currentDescription) {
    return {
      label: "Regenerate " + textName,
      icon: "✨"
    };
  }
  return {
    label: "Generate " + textName,
    icon: "✨"
  };
};
const FitContentBrandButton = styled(BrandButton)`
  width: fit-content;
`;
const setIsLoading = (isLoading, state) => ({
  ...state,
  isLoading
});
const setError = (state, errorMessage) => ({
  ...state,
  hasError: true,
  errorMessage: errorMessage ?? DEFAULT_ERROR_MESSAGE
});
const setReasonForNotBeingAbleToGenerateDescription = (state, reasonForNotBeingAbleToGenerateDescription) => ({
  ...state,
  reasonForNotBeingAbleToGenerateDescription
});
const defaultUseGenerateDescriptionState = {
  isLoading: false,
  hasError: false
};
const resetState = () => ({
  ...defaultUseGenerateDescriptionState
});
const useGenerateDescriptionState = () => {
  const [state, setState] = useState(
    defaultUseGenerateDescriptionState
  );
  const commands = {
    onLoading: () => setState(setIsLoading(true, state)),
    onError: (errorMessage) => setState((state2) => setError(state2, errorMessage)),
    onDescriptionReceived: () => setState(resetState()),
    onLLMCouldNotGenerateDescription: (reasonForNotBeingAbleToGenerateDescription) => setState(
      setReasonForNotBeingAbleToGenerateDescription(
        state,
        reasonForNotBeingAbleToGenerateDescription
      )
    )
  };
  return {
    state,
    commands
  };
};
const GenerateDescriptionButton = ({
  onClick,
  ...props
}) => {
  const { label, icon, ...buttonProps } = getButtonProps(props);
  return /* @__PURE__ */ jsx(FitContentBrandButton, { onClick, ...buttonProps, children: `${icon ?? ""} ${label}` });
};
const InnerIsland = ({
  title,
  description,
  children
}) => /* @__PURE__ */ jsx(
  Box,
  {
    padding: "medium",
    backgroundColor: "surfaceStatusNeutral",
    borderRadius: "r8",
    children: /* @__PURE__ */ jsxs(Stack, { gap: "medium", children: [
      /* @__PURE__ */ jsxs(Stack, { gap: "small", children: [
        /* @__PURE__ */ jsx(Header4, { children: title }),
        description && /* @__PURE__ */ jsx(InlineText, { variant: "text2", color: "textModerate", children: description })
      ] }),
      children
    ] })
  }
);
const LoaderWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${colors.surfaceDefault};
`;
const AnimationContainer = styled.div`
  width: 350px;
  height: 4px;
  background-color: ${colors.borderAction};
  border-radius: 10px;
  margin-top: 24px;
  position: relative;
  overflow: hidden;
`;
const MovingBar = styled.div`
  @keyframes loader-move {
    from {
      transform: translateX(-100%);
    }

    to {
      transform: translateX(100%);
    }
  }

  background: ${colors.surfaceBrandStrong};
  border-radius: 4px;
  height: 100%;
  width: 100%;
  animation-duration: 1.5s;
  animation-name: loader-move;
  animation-iteration-count: infinite;
`;
const ReportBuilderLoader = () => {
  return /* @__PURE__ */ jsxs(LoaderWrapper, { children: [
    /* @__PURE__ */ jsx(
      SvgImage,
      {
        svgImageName: SvgImageName.INVERTED_ARDOQ_LOGO,
        color: colors.surfaceActionBrand
      }
    ),
    /* @__PURE__ */ jsx(AnimationContainer, { children: /* @__PURE__ */ jsx(MovingBar, {}) })
  ] });
};
const DoqLoader = ({
  title = "Loading data...",
  doqSize = DoqSize.MEDIUM
}) => {
  return /* @__PURE__ */ jsx(Box, { width: "full", height: "full", children: /* @__PURE__ */ jsxs(Stack, { align: "center", justify: "center", gap: "large", children: [
    /* @__PURE__ */ jsx(Header2, { children: title }),
    /* @__PURE__ */ jsx(
      DoqLoadingAnimation,
      {
        delay: 700,
        doqSize,
        doqs: [
          DoqType.SIMPLE,
          DoqType.ALGORITHM,
          DoqType.SUCCESS,
          DoqType.COMPONENT_MERGE_EMPTY
        ]
      }
    )
  ] }) });
};
const AdvancedNavigationMenu = ({
  children,
  menuId,
  ariaLabelledBy,
  ariaLabel
}) => {
  return /* @__PURE__ */ jsx("nav", { children: /* @__PURE__ */ jsx(
    MenuWrapper,
    {
      id: menuId,
      role: "menu",
      "aria-labelledby": ariaLabelledBy,
      "aria-label": ariaLabel,
      children
    }
  ) });
};
const MenuWrapper = styled$1.ul`
  border: 1px solid ${colors.borderAction};
  border-radius: ${radius.r16};
  list-style-type: none;
  margin: 0;
  padding: ${s8};
  display: flex;
  flex-direction: column;
`;
const AdvancedNavigationMenuItem = ({
  label,
  isSelected,
  isDisabled,
  iconName,
  rightContent,
  description,
  onClick,
  popoverContent,
  selectedItemIndex,
  currentItemIndex
}) => {
  const menuItemLabelId = useId();
  if (isSelected) {
    return /* @__PURE__ */ jsx("li", { role: "presentation", children: /* @__PURE__ */ jsxs(
      ActiveMenuItemButton,
      {
        onClick,
        disabled: isDisabled,
        role: "menuitem",
        "aria-labelledby": menuItemLabelId,
        children: [
          /* @__PURE__ */ jsx(
            MenuMainRow,
            {
              menuItemLabelId,
              iconName,
              label,
              rightContent,
              isActive: true
            }
          ),
          /* @__PURE__ */ jsx(MenuDescription, { children: description })
        ]
      }
    ) });
  }
  const isNextItemSelected = currentItemIndex + 1 === selectedItemIndex;
  return /* @__PURE__ */ jsx(ListItem, { $isNextItemSelected: isNextItemSelected, children: /* @__PURE__ */ jsx(WithPopover, { content: popoverContent, children: /* @__PURE__ */ jsx(
    MenuItemButton,
    {
      onClick,
      disabled: isDisabled,
      role: "menuitem",
      "aria-labelledby": menuItemLabelId,
      "aria-disabled": isDisabled,
      children: /* @__PURE__ */ jsx(
        MenuMainRow,
        {
          iconName,
          label,
          rightContent,
          isActive: false,
          isDisabled,
          menuItemLabelId
        }
      )
    }
  ) }) });
};
const MenuMainRow = ({
  iconName,
  label,
  rightContent,
  isActive,
  isDisabled = false,
  menuItemLabelId
}) => {
  const fontColor = isActive ? colors.textModerate : isDisabled ? colors.textDisabled : colors.textSubtle;
  const iconColor = isActive ? colors.iconActionBrand : isDisabled ? colors.iconActionDisabled : colors.icon;
  return /* @__PURE__ */ jsxs(FlexBox, { gap: "small", align: "center", children: [
    /* @__PURE__ */ jsx(IconWrapper, { $iconColor: iconColor, children: /* @__PURE__ */ jsx(Icon, { iconSize: IconSize.MEDIUM, iconName }) }),
    /* @__PURE__ */ jsx(Label, { id: menuItemLabelId, $fontColor: fontColor, children: label }),
    /* @__PURE__ */ jsx(RightContentWrapper, { children: rightContent })
  ] });
};
const IconWrapper = styled$1.span`
  color: ${({ $iconColor }) => $iconColor};
`;
const RightContentWrapper = styled$1.div`
  max-width: 200px;
`;
const Label = styled$1.span`
  flex-grow: 1;
  color: ${({ $fontColor }) => $fontColor};
  ${header4};
  text-align: start;
  white-space: nowrap;
`;
const MenuItemButton = styled$1.button`
  padding-left: ${s8};
  padding-right: ${s8};
  padding-top: ${s16};
  padding-bottom: ${s16};
  background-color: transparent;
  width: 100%;
  border: none;
  border: 2px solid transparent;
  border-radius: ${radius.r8};

  /* hovering over the button changes the color of the icon */
  &:not([disabled]):hover,
  &:not([disabled]):focus {
    ${IconWrapper} {
      color: ${colors.iconActionBrandHover};
    }
  }

  &:not([disabled]):focus-visible {
    border-color: ${colors.borderFocus};
  }
`;
const ActiveMenuItemButton = styled$1(MenuItemButton)`
  background-color: ${colors.surfaceStatusNeutral};
  border-radius: ${radius.r8};
  padding-bottom: ${s8};
`;
const ListItem = styled$1.li`
  border: none;

  &:not(:last-of-type) {
    ${({ $isNextItemSelected }) => !$isNextItemSelected && `border-bottom: 1px solid ${colors.borderAction};`}
  }
`;
const MenuDescription = styled$1.p`
  padding-top: ${s8};
  ${caption};
  text-align: start;
`;
const TRANSITION_TIME = 350;
const MOUNT_TIME = 500;
const fullScreenCSS = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
`;
const DrawerBackdrop = styled.div`
  ${fullScreenCSS};
  user-select: none;
  background-color: rgba(0, 0, 0, 0.2);

  /* ANIMATION */
  transition: opacity ${TRANSITION_TIME}ms ease;
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
`;
const DrawerContentContainer = styled.div`
  z-index: 1;
  pointer-events: auto;
  &:focus {
    outline: none;
  }

  /* ANIMATION */
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
  transform: ${({ $isVisible, $orientation }) => $orientation === "vertical" ? `translateX(${$isVisible ? 0 : 100}%)` : `translateY(${$isVisible ? 0 : 100}%)`};
  transition:
    opacity ${TRANSITION_TIME}ms ease,
    transform ${TRANSITION_TIME}ms ease;
`;
const DrawerScroll = styled.div`
  overflow-y: auto;
  height: 100%;
`;
const DrawerContainer = ({
  isVisible,
  orientation,
  onBackdropClick,
  onEscPress = () => {
  },
  children
}) => {
  useKeydown(handleEscapeButtonPressed(onEscPress));
  return /* @__PURE__ */ jsx(FullscreenDiv, { $isDrawer: true, children: /* @__PURE__ */ jsxs(FlexBox, { justify: "flex-end", align: "flex-end", width: "full", height: "full", children: [
    /* @__PURE__ */ jsx(DrawerBackdrop, { onClick: onBackdropClick, $isVisible: isVisible }),
    /* @__PURE__ */ jsx(
      DrawerContentContainer,
      {
        role: "modal",
        $isVisible: isVisible,
        $orientation: orientation,
        children
      }
    )
  ] }) });
};
const useMountTransition = (isMounted, mountDelay) => {
  const [hasTransitionedIn, setHasTransitionedIn] = useState(false);
  useEffect(() => {
    const timeoutId = setTimeout(
      () => setHasTransitionedIn(isMounted),
      mountDelay
    );
    return () => {
      clearTimeout(timeoutId);
    };
  }, [mountDelay, isMounted, hasTransitionedIn]);
  return hasTransitionedIn;
};
const DrawerCloseButton = ({ onClose }) => {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      iconName: IconName.CLOSE,
      onClick: onClose,
      "data-tooltoip-text": "Close",
      dataTestId: "drawer-header-close-button"
    }
  );
};
const DrawerProvider = ({
  shouldCloseOnEscapeKey = false,
  shouldCloseOnBackdropClick = false,
  closeDrawer,
  renderDrawer
}) => {
  const [showDrawer2, setShowDrawer] = useState(true);
  const hasTransitionedIn = useMountTransition(showDrawer2, TRANSITION_TIME);
  const handleCloserDrawer = () => {
    setShowDrawer(false);
    closeDrawer();
  };
  const providedDrawerComponent = renderDrawer({ handleCloserDrawer });
  const orientation = providedDrawerComponent.props.orientation || "horizontal";
  const drawerComponent = cloneElement(providedDrawerComponent, {
    controls: providedDrawerComponent.props.controls ?? /* @__PURE__ */ jsx(DrawerCloseButton, { onClose: handleCloserDrawer })
  });
  return showDrawer2 || hasTransitionedIn ? /* @__PURE__ */ jsx(
    DrawerContainer,
    {
      orientation,
      onEscPress: async () => await resolveBoolean(shouldCloseOnEscapeKey) && handleCloserDrawer(),
      onBackdropClick: async () => await resolveBoolean(shouldCloseOnBackdropClick) && handleCloserDrawer(),
      isVisible: showDrawer2 && hasTransitionedIn,
      children: drawerComponent
    }
  ) : null;
};
const DrawerHeader = ({
  title,
  icon,
  subtitle,
  controls
}) => {
  return /* @__PURE__ */ jsxs(
    FlexBox,
    {
      justify: "space-between",
      paddingTop: "large",
      paddingX: "large",
      align: "center",
      children: [
        /* @__PURE__ */ jsxs(Stack, { gap: "small", children: [
          /* @__PURE__ */ jsxs(FlexBox, { gap: "small", children: [
            icon,
            /* @__PURE__ */ jsx(Header2, { children: title })
          ] }),
          subtitle && /* @__PURE__ */ jsx(Text, { variant: "text2", color: "textSubtle", children: subtitle })
        ] }),
        /* @__PURE__ */ jsx(FlexBox, { gap: "small", align: "center", children: controls })
      ]
    }
  );
};
const getHeightOfDrawer = (drawerSize, orientation) => {
  if (orientation === "vertical") return "100vh";
  if (drawerSize === "small") return "49vh";
  if (drawerSize === "medium") return "67vh";
  return "85vh";
};
const getWidthOfDrawer = (drawerSize, orientation) => {
  if (orientation === "horizontal") return "100vw";
  if (drawerSize === "small") return "20vw";
  if (drawerSize === "medium") return "67vw";
  return "85vw";
};
const getBorderRadius = (orientation) => {
  return orientation === "vertical" ? `${radius.r16} 0px 0px ${radius.r16}` : `${radius.r16} ${radius.r16} 0px 0px`;
};
const DrawerWrapperStyle = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${({ $orientation }) => getBorderRadius($orientation)};
  box-shadow: ${shadowM};
  overflow: hidden;
  background-color: ${colors.surfaceDefault};
  width: ${({ $drawerSize, $orientation }) => getWidthOfDrawer($drawerSize, $orientation)};
  min-height: 240px;
  min-width: 480px;
  gap: ${spacing.s24};
  height: ${({ $drawerSize, $orientation }) => getHeightOfDrawer($drawerSize, $orientation)};
`;
const DrawerWrapper = ({
  drawerSize = "medium",
  orientation = "horizontal",
  children
}) => /* @__PURE__ */ jsx(DrawerWrapperStyle, { $drawerSize: drawerSize, $orientation: orientation, children });
const showDrawer = ({
  stackPageName,
  shouldCloseOnEscapeKey = false,
  shouldCloseOnBackdropClick = false,
  renderDrawer,
  onClose = () => {
  }
}) => {
  const closeDrawer = () => {
    setTimeout(() => {
      popStackPageByName(stackPageName);
    }, MOUNT_TIME);
    onClose();
  };
  pushStackPage(
    () => /* @__PURE__ */ jsx(
      DrawerProvider,
      {
        shouldCloseOnEscapeKey,
        shouldCloseOnBackdropClick,
        closeDrawer,
        renderDrawer
      }
    ),
    {
      stackPageName
    }
  );
  return {
    closeDrawer
  };
};
const DrawerMainContent = ({
  children,
  hasHeader,
  paddingX,
  paddingBottom
}) => /* @__PURE__ */ jsx(
  Box,
  {
    paddingX: paddingX ?? "large",
    paddingBottom: paddingBottom ?? "large",
    paddingTop: hasHeader ? "none" : "large",
    height: "full",
    children
  }
);
const DrawerBody = ({
  children,
  hideScroll,
  hasHeader,
  paddingX,
  paddingBottom
}) => {
  return hideScroll ? /* @__PURE__ */ jsx(
    DrawerMainContent,
    {
      hasHeader,
      paddingX,
      paddingBottom,
      children
    }
  ) : /* @__PURE__ */ jsx(DrawerScroll, { children: /* @__PURE__ */ jsx(
    DrawerMainContent,
    {
      hasHeader,
      paddingX,
      paddingBottom,
      children
    }
  ) });
};
const Drawer = ({
  drawerSize,
  orientation,
  title,
  icon,
  subtitle,
  controls,
  children,
  hideScroll,
  paddingX,
  paddingBottom
}) => {
  return /* @__PURE__ */ jsxs(DrawerWrapper, { drawerSize, orientation, children: [
    Boolean(title) && /* @__PURE__ */ jsx(
      DrawerHeader,
      {
        title,
        icon,
        subtitle,
        controls
      }
    ),
    /* @__PURE__ */ jsx(
      DrawerBody,
      {
        hideScroll,
        hasHeader: Boolean(title),
        paddingX,
        paddingBottom,
        children
      }
    )
  ] });
};
const DrawerDivider = styled.hr`
  margin: 0 -${space.large};
  overflow: auto;
  border-top: 1px solid ${colors.borderSubtle00};
`;
const Centered = ({
  height,
  children,
  className
}) => /* @__PURE__ */ jsx(
  FlexBox,
  {
    height: "full",
    align: "center",
    justify: "center",
    style: height ? { height } : void 0,
    className,
    children
  }
);
const FooterLink = ({ label, href }) => /* @__PURE__ */ jsx(Link, { hideIcon: true, typography: "text2", underlineOnHover: true, href, children: label });
const CopyrightFooter = () => /* @__PURE__ */ jsxs(Text, { color: "textSubtle", children: [
  "2013-",
  currentYear(),
  " © Ardoq AS, All Rights Reserved.",
  " ",
  /* @__PURE__ */ jsx(FooterLink, { label: "Privacy Notice", href: "https://ardoq.com/privacy/" }),
  " |",
  " ",
  /* @__PURE__ */ jsx(FooterLink, { label: "Terms of Service", href: "https://ardoq.com/terms/" })
] });
const ArdoqErrorNotification = ({
  error
}) => {
  const message = getArdoqErrorMessage(error);
  const traceId = getArdoqErrorTraceId(error);
  return /* @__PURE__ */ jsxs(ErrorNotification, { children: [
    /* @__PURE__ */ jsx(Paragraph, { children: message }),
    traceId && /* @__PURE__ */ jsx(Box, { marginTop: "small", children: /* @__PURE__ */ jsxs(Paragraph, { children: [
      /* @__PURE__ */ jsx(InlineText, { variant: "text2Bold", children: "Trace ID: " }),
      traceId
    ] }) })
  ] });
};
export {
  AdvancedNavigationMenu,
  AdvancedNavigationMenuItem,
  ArdoqErrorNotification,
  BadgeWithCounter,
  BigButton,
  Centered,
  ClickableIslandHeader,
  ColorBarBigButton,
  CopyrightFooter,
  DoqLoader,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerDivider,
  DrawerHeader,
  GenerateDescriptionButton,
  InnerIsland,
  LineSeparatorWithText,
  ReportBuilderLoader,
  SearchAndFilterBar,
  TextAreaWithCounter,
  TriStateSwitch,
  showDrawer,
  useGenerateDescriptionState
};
//# sourceMappingURL=index.js.map
